import type { ChangeEvent } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    makeStyles,
} from '@material-ui/core';

import { Conditional } from '@klp/conditional';
import { Block, Heading } from '@klp/design';
import { ActionButton } from '@klp/design/ActionButton';

import type { User } from '../classes/User';
import { useAddCustomerMutation } from '../rtkApi';

const useStyles = makeStyles(() => ({
    formControl: {
        width: '100%',
        minWidth: '240px',
    },
}));

interface IAddCustomerProps {
    availableCustomers: User['customers'];
    selectedUser: User;
    onSuccess?: () => void;
}

export const AddCustomer: React.FC<IAddCustomerProps> = ({
    availableCustomers,
    selectedUser,
    onSuccess,
}) => {
    const { t } = useTranslation();
    const [displayDialog, setDisplayDialog] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const classes = useStyles();
    const [addCustomer] = useAddCustomerMutation();

    const openDialog = () => {
        setDisplayDialog(true);
    };

    const closeDialog = () => {
        if (!isLoading) {
            setDisplayDialog(false);
            setSelectedCustomer('');
        }
    };

    const selectCustomer = (event: ChangeEvent<{ value: unknown }>) => {
        setSelectedCustomer(event.target.value as string);
    };

    const handleAddCustomer = async () => {
        setIsLoading(true);
        try {
            await addCustomer({
                customerNumber: selectedCustomer,
                email: selectedUser.email,
            }).unwrap();
            if (onSuccess) {
                onSuccess();
            }
            setDisplayDialog(false);
            setSelectedCustomer('');
        } catch (error) {
            // Error handling is done by RTK Query
        } finally {
            setIsLoading(false);
        }
    };

    const customersToAdd = availableCustomers.filter(
        (customer) => !selectedUser.customers.includes(customer),
    );

    return (
        <Conditional condition={customersToAdd.length > 0}>
            <Block>
                <ActionButton
                    actionType="forward"
                    translation="userSearch.addCustomer"
                    klpTypographyVariant="small"
                    clickHandler={openDialog}
                />
            </Block>

            <Dialog open={displayDialog} onClose={closeDialog}>
                <DialogContent>
                    <DialogTitle disableTypography={true}>
                        <Heading
                            klpHeadingLevel="THREE"
                            translation="userSearch.addCustomerTitle"
                        />
                    </DialogTitle>
                    <DialogContent>
                        <FormControl className={classes.formControl}>
                            <InputLabel>
                                {t('clientSelect.chooseClient')}
                            </InputLabel>
                            <Select
                                value={selectedCustomer}
                                onChange={selectCustomer}
                            >
                                {customersToAdd.map((customer: string) => (
                                    <MenuItem key={customer} value={customer}>
                                        {customer}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions>
                        <ActionButton
                            actionType="cancel"
                            onClick={closeDialog}
                            translation="general.cancel"
                            disabled={isLoading}
                        />
                        <ActionButton
                            actionType="forward"
                            onClick={handleAddCustomer}
                            translation="general.save"
                            disabled={!selectedCustomer}
                            isLoading={isLoading}
                        />
                    </DialogActions>
                </DialogContent>
            </Dialog>
        </Conditional>
    );
};
